/**
 * Semi wide-load catalog — heavy machinery on a low flat bed.
 * Independent of Hotshot light-deck / gooseneck and of the Operator Desk
 * Semi board (truck water / oil / caliche).
 */

export type SemiSiteType = 'residential' | 'commercial' | 'oilfield';

export type SemiMachineId =
  | 'dozer'
  | 'excavator'
  | 'loader'
  | 'motor_grader'
  | 'frac_iron'
  | 'tank'
  | 'other';

export type SemiTrailerId = 'lowboy' | 'rgn' | 'step_deck';

export type SemiLoadMethod = 'drive_on' | 'crane' | 'forklift' | 'ramps';

export type SemiPermitBy = 'customer' | 'hauler';

export const SEMI_SITE_TYPE_OPTIONS: ReadonlyArray<{
  id: SemiSiteType;
  label: string;
  subtitle: string;
}> = Object.freeze([
  { id: 'residential', label: 'Residential', subtitle: 'House, ranch, or shop at a street address' },
  { id: 'commercial', label: 'Business / commercial', subtitle: 'Yard, warehouse, storefront, or shop' },
  { id: 'oilfield', label: 'Oilfield', subtitle: 'Pad / lease / CTB — API # or facility' },
]);

export const SEMI_MACHINE_OPTIONS: ReadonlyArray<{
  id: SemiMachineId;
  label: string;
  subtitle: string;
}> = Object.freeze([
  { id: 'dozer', label: 'Dozer', subtitle: 'Track dozer / crawler' },
  { id: 'excavator', label: 'Excavator', subtitle: 'Trackhoe / excavator' },
  { id: 'loader', label: 'Loader', subtitle: 'Wheel loader / backhoe' },
  { id: 'motor_grader', label: 'Motor grader', subtitle: 'Blade / maintainer' },
  { id: 'frac_iron', label: 'Frac iron / pump', subtitle: 'Pump, blender, or iron' },
  { id: 'tank', label: 'Tank / vessel', subtitle: 'Frac tank, separator, vessel' },
  { id: 'other', label: 'Other machinery', subtitle: 'Describe it on the next line' },
]);

export const SEMI_TRAILER_OPTIONS: ReadonlyArray<{
  id: SemiTrailerId;
  label: string;
  subtitle: string;
}> = Object.freeze([
  { id: 'lowboy', label: 'Lowboy', subtitle: 'Low flat bed — typical construction iron' },
  { id: 'rgn', label: 'RGN', subtitle: 'Detachable gooseneck — drive-on heavy iron' },
  { id: 'step_deck', label: 'Step-deck', subtitle: 'Drop-deck when height is the problem' },
]);

export const SEMI_LOAD_METHOD_OPTIONS: ReadonlyArray<{
  id: SemiLoadMethod;
  label: string;
  hint: string;
}> = Object.freeze([
  { id: 'drive_on', label: 'Drive on / off', hint: 'Machine walks the ramps under its own power' },
  { id: 'crane', label: 'Crane on site', hint: 'Site provides a picker at that end' },
  { id: 'forklift', label: 'Forklift', hint: 'Forks at the yard or pad' },
  { id: 'ramps', label: 'Ramps only', hint: 'Winch / roll — no crane' },
]);

export const SEMI_WEIGHT_BANDS = [
  'Under 20,000 lbs',
  '20,000 – 40,000 lbs',
  '40,000 – 80,000 lbs',
  '80,000 lbs +',
] as const;

/** Legal US width without an oversize permit. */
export const SEMI_LEGAL_WIDTH_FT = 8.5;
/** Typical escort trigger (TX / NM oversize). */
export const SEMI_ESCORT_WIDTH_FT = 12;
export const SEMI_ESCORT_HEIGHT_FT = 14;

export function isSemiSiteType(raw: unknown): raw is SemiSiteType {
  return raw === 'residential' || raw === 'commercial' || raw === 'oilfield';
}

export function isSemiMachineId(raw: unknown): raw is SemiMachineId {
  return SEMI_MACHINE_OPTIONS.some((o) => o.id === raw);
}

export function isSemiTrailerId(raw: unknown): raw is SemiTrailerId {
  return raw === 'lowboy' || raw === 'rgn' || raw === 'step_deck';
}

export function isSemiLoadMethod(raw: unknown): raw is SemiLoadMethod {
  return raw === 'drive_on' || raw === 'crane' || raw === 'forklift' || raw === 'ramps';
}

export function isSemiPermitBy(raw: unknown): raw is SemiPermitBy {
  return raw === 'customer' || raw === 'hauler';
}

export function clampSemiFeet(raw: unknown, fallback: number, max = 80): number {
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0) return fallback;
  return Math.min(max, Math.round(n * 10) / 10);
}

export function semiIsOversize(widthFt: number, heightFt: number): boolean {
  return widthFt > SEMI_LEGAL_WIDTH_FT || heightFt > 13.5;
}

export function semiNeedsEscort(widthFt: number, heightFt: number): boolean {
  return widthFt >= SEMI_ESCORT_WIDTH_FT || heightFt >= SEMI_ESCORT_HEIGHT_FT;
}
